import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FormGroup, UntypedFormBuilder } from '@angular/forms';
import { Client } from '@core/types/client';
import { ApisService } from '@app/core/services/apis-service';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { PepRawJsonDialogComponent } from './pep-raw-json-dialog/pep-raw-json-dialog.component';
import { PepRecord } from './pep-record.interface';
import { ContactService } from '@app/core/services/contacts.service';
import {
  ContactPhone,
  ContactPhoneDto,
  ContactPhoneType,
} from '@app/core/types/contact-phone';
import { forkJoin, of } from 'rxjs';
import { catchError, mergeMap, tap } from 'rxjs/operators';
import { TagService } from '@app/core/services/tag.service';
import { Tag } from '@app/core/types/tag';
import { PepCheckResult } from '@app/core/types/pep-check-result';

@Component({
  selector: 'itfg-risk-indicators-edit',
  templateUrl: './risk-indicators-edit.component.html',
  styleUrls: ['./risk-indicators-edit.component.scss'],
  standalone: false,
})
export class RiskIndicatorsEditComponent implements OnInit, OnChanges {
  @Input() client: Client;
  @Input() isNewItem: boolean;

  riskIndicatorsForm: FormGroup;

  pepMatch: boolean | null = null;
  relatedPepMatch: boolean | null = null;
  relatedPepName: string | null = null;
  pepReportData: PepRecord[] = [];
  showPepReport: boolean = false;
  isLoadingPepReport: boolean = false;
  pepReportRawData: any = null;
  pepError: boolean = false;
  pepReportError: boolean = false;

  private hasInitialized = false;

  // Regdata fields
  seizureMatch: boolean | null = null;
  napMatch: boolean | null = null;
  regdataError: boolean = false;
  lastReportDate: string | null = null;

  // Constants
  private readonly TAGS_LIMIT = 15;
  private readonly PEP_TAG_CODE = 'POLITICALLY_EXPOSED_PERSON';
  private readonly RELATED_TO_PEP_TAG_CODE = 'RELATED_TO_PEP';

  constructor(
    private fb: UntypedFormBuilder,
    private apisService: ApisService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private contactService: ContactService,
    private tagService: TagService
  ) {}

  ngOnInit() {
    this.createForm();
    this.hasInitialized = true;
    if (this.client) {
      this.checkPepStatus();
      this.searchRegdata();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.client || !this.client) {
      return;
    }

    if (!this.hasInitialized || changes.client.firstChange) {
      return;
    }

    this.checkPepStatus();
    this.searchRegdata();
  }

  checkPepStatus() {
    if (this.client && (this.client.civilId || this.client.fullName)) {
      this.apisService
        .checkPepStatus(this.client.civilId, this.client.fullName)
        .subscribe({
          next: (result: PepCheckResult) => {
            this.pepError = false;
            this.pepMatch = result.isPep && result.isDirect;
            this.relatedPepMatch = result.isPep && !result.isDirect;
            this.relatedPepName = result.relatedName;

            // Direct PEP field
            const pepKey = this.pepMatch
              ? 'riskIndicators.match'
              : 'riskIndicators.nomatch';
            this.translate.get(pepKey).subscribe((res) => {
              this.riskIndicatorsForm.patchValue({ pep: res });
            });

            // Related-to-PEP field
            if (this.relatedPepMatch) {
              const yesText = this.translate.instant('riskIndicators.match');
              const value = this.relatedPepName
                ? `${yesText} — ${this.relatedPepName}`
                : yesText;
              this.riskIndicatorsForm.patchValue({ pepRelated: value });
            } else {
              this.translate.get('riskIndicators.nomatch').subscribe((res) => {
                this.riskIndicatorsForm.patchValue({ pepRelated: res });
              });
            }

            // Apply the right tag based on classification
            if (this.pepMatch) {
              this.addTagIfNeeded(this.PEP_TAG_CODE);
            } else if (this.relatedPepMatch) {
              this.addTagIfNeeded(this.RELATED_TO_PEP_TAG_CODE);
            }
          },
          error: (error) => {
            console.error('Error fetching PEP status:', error);
            this.pepError = true;
            this.pepMatch = null;
            this.relatedPepMatch = null;
            this.relatedPepName = null;
            const errorText = this.getApisUnavailableText();
            this.riskIndicatorsForm.patchValue({
              pep: errorText,
              pepRelated: errorText,
            });
          },
        });
    }
  }

  searchRegdata(force: boolean = false) {
    if (this.client && this.client.civilId && this.client.mobile) {
      this.apisService
        .searchRegdata(this.client.civilId, this.client.mobile, force)
        .subscribe({
          next: (result) => {
            this.regdataError = false;
            this.lastReportDate = result.lastReportDate || null;
            console.log('Regdata result:', result);
            // Update company participation
            this.riskIndicatorsForm.patchValue({
              companyParticipation: result.numberOfCompaniesParticipating || 0,
            });

            // Update seizure field
            this.seizureMatch = result.hasGeneralSeizureEntry;
            const seizureKey = result.hasGeneralSeizureEntry
              ? 'riskIndicators.match'
              : 'riskIndicators.nomatch';
            this.translate.get(seizureKey).subscribe((res) => {
              this.riskIndicatorsForm.patchValue({ seizure: res });
            });

            // Update NAP field
            this.napMatch = result.hasNapEntry;
            const napKey = result.hasNapEntry
              ? 'riskIndicators.match'
              : 'riskIndicators.nomatch';
            this.translate.get(napKey).subscribe((res) => {
              this.riskIndicatorsForm.patchValue({ nap: res });
            });

            // Update phone match field
            this.riskIndicatorsForm.patchValue({
              phoneMatch: result.phoneMatch || '',
            });

            this.syncRegdataContacts(result?.contacts);
          },
          error: (error) => {
            console.error('Error fetching regdata:', error);
            this.regdataError = true;
            this.seizureMatch = null;
            this.napMatch = null;
            const errorText = this.getApisUnavailableText();
            this.riskIndicatorsForm.patchValue({
              companyParticipation: errorText,
              seizure: errorText,
              nap: errorText,
              phoneMatch: errorText,
            });
          },
        });
    }
  }

  onRefreshRegdata() {
    this.searchRegdata(true);
  }

  private syncRegdataContacts(regdataContacts: any[]) {
    if (
      !this.client?.id ||
      !Array.isArray(regdataContacts) ||
      regdataContacts.length === 0
    ) {
      return;
    }

    const normalizedContacts = this.normalizeRegdataContacts(regdataContacts);
    if (normalizedContacts.length === 0) {
      return;
    }

    forkJoin({
      existingContacts: this.contactService.getContactList(this.client.id),
      contactTypes: this.contactService.getContactTypes(this.client.id),
    }).subscribe({
      next: ({ existingContacts, contactTypes }) => {
        const otherType = this.getOtherContactType(contactTypes);
        if (!otherType) {
          return;
        }

        const missingContacts = normalizedContacts.filter(
          (regContact) =>
            !this.hasExistingMatch(
              existingContacts,
              regContact.phone,
              regContact.eik
            )
        );

        if (missingContacts.length === 0) {
          return;
        }

        const createRequests = missingContacts.map((contact) => {
          const contactDto: ContactPhoneDto = {
            phone: contact.phone,
            description: this.normalizeDescription(contact.description),
            type: otherType,
            active: false,
          };

          return this.contactService
            .createContact(this.client.id, contactDto)
            .pipe(
              catchError((error) => {
                console.error('Error creating regdata contact:', error);
                return of(null);
              })
            );
        });

        forkJoin(createRequests).subscribe({
          next: (createdContacts) => {
            if (createdContacts.some((contact) => !!contact)) {
              this.contactService.getContactList(this.client.id).subscribe();
            }
          },
        });
      },
      error: (error) => {
        console.error('Error preparing regdata contacts sync:', error);
      },
    });
  }

  private normalizeRegdataContacts(
    regdataContacts: any[]
  ): Array<{ phone: string; eik: string; description: string }> {
    const uniqueByPhoneAndEik = new Set<string>();

    return regdataContacts
      .filter((contact) => !!contact?.phone && !!contact?.eik)
      .map((contact) => ({
        phone: this.normalizePhone(contact.phone),
        eik: String(contact.eik).trim(),
        description: String(contact.description || '').trim(),
      }))
      .filter((contact) => {
        if (
          !contact.phone ||
          !contact.eik ||
          !this.isPhoneValidForManualSave(contact.phone)
        ) {
          return false;
        }

        const key = `${contact.phone}::${contact.eik}`;
        if (uniqueByPhoneAndEik.has(key)) {
          return false;
        }
        uniqueByPhoneAndEik.add(key);

        return true;
      });
  }

  private isPhoneValidForManualSave(phone: string): boolean {
    return (
      /^\+359[0-9]*$/.test(phone) && phone.length >= 10 && phone.length <= 13
    );
  }

  private normalizeDescription(description: string): string {
    const trimmedDescription = String(description || '').trim();

    if (trimmedDescription.length <= 64) {
      return trimmedDescription;
    }

    // Contact description on edit screen is max 64 chars.
    return trimmedDescription.slice(0, 64);
  }

  private normalizePhone(phone: string): string {
    return String(phone || '')
      .replace(/\s+/g, '')
      .trim();
  }

  private getOtherContactType(
    contactTypes: ContactPhoneType[]
  ): ContactPhoneType | null {
    if (!Array.isArray(contactTypes) || contactTypes.length === 0) {
      return null;
    }

    const otherType = contactTypes.find(
      (type) => (type?.code || '').toUpperCase() === 'OTHER'
    );
    return otherType || contactTypes[0];
  }

  private hasExistingMatch(
    existingContacts: ContactPhone[],
    phone: string,
    eik: string
  ): boolean {
    return existingContacts.some((contact) => {
      const existingPhone = this.normalizePhone(contact?.phone || '');
      const description = String(contact?.description || '');
      return existingPhone === phone && description.includes(eik);
    });
  }

  onInfoClick() {
    // Toggle visibility
    this.showPepReport = !this.showPepReport;

    // Fetch data if opening (removed cache check for debugging)
    if (this.showPepReport && this.client && this.client.civilId) {
      this.isLoadingPepReport = true;
      this.pepReportError = false;
      this.apisService.getPepReport(this.client.civilId).subscribe({
        next: (report) => {
          // Store raw data for JSON display
          this.pepReportRawData = report;
          // Extract records array from report.data.records
          const allRecords = report?.data?.records || [];
          // Filter to only show records that are active or within last 12 months
          const relevantRecords = allRecords.filter((record: PepRecord) =>
            this.isRecordRelevant(record.toDate)
          );
          // Remove duplicates by position+institution, keep the most recent value by recordDate
          this.pepReportData = this.formatPepTableRows(
            this.filterByLatestRecordDate(relevantRecords)
          );
          this.isLoadingPepReport = false;
          this.pepReportError = false;
        },
        error: (error) => {
          console.error('Error fetching PEP report:', error);
          this.isLoadingPepReport = false;
          this.pepReportError = true;
          this.pepReportRawData = null;
          this.pepReportData = [];
        },
      });
    }
  }

  private isRecordRelevant(toDate: string | null): boolean {
    // Show if toDate is null or empty (active)
    if (!toDate) {
      return true;
    }

    // Show if toDate is in the future or within last 12 months
    const toDateObj = new Date(toDate);
    const today = new Date();
    const twelveMonthsAgo = new Date();
    twelveMonthsAgo.setMonth(today.getMonth() - 12);

    // Include if toDate >= 12 months ago (this includes future dates)
    return toDateObj >= twelveMonthsAgo;
  }

  private filterByLatestRecordDate(records: PepRecord[]): PepRecord[] {
    const byKey = new Map<string, PepRecord>();

    records.forEach((record) => {
      const key = `${record.xmlPosition || ''}||${record.xmlWork || ''}`;
      const existing = byKey.get(key);

      if (!existing) {
        byKey.set(key, record);
        return;
      }

      const existingDate = this.parseDateOrMin(existing.recordDate);
      const currentDate = this.parseDateOrMin(record.recordDate);
      if (currentDate >= existingDate) {
        byKey.set(key, record);
      }
    });

    return Array.from(byKey.values());
  }

  private formatPepTableRows(records: PepRecord[]): PepRecord[] {
    return records.map((record) => ({
      ...record,
      xmlPosition: this.toSingleLeadingCapitalLetter(record.xmlPosition),
      xmlWork: this.toSingleLeadingCapitalLetter(record.xmlWork),
    }));
  }

  private toSingleLeadingCapitalLetter(value?: string | null): string {
    const normalized = String(value || '')
      .trim()
      .toLocaleLowerCase('bg-BG');
    if (!normalized) {
      return '';
    }

    return (
      normalized.charAt(0).toLocaleUpperCase('bg-BG') + normalized.slice(1)
    );
  }

  private parseDateOrMin(value?: string | null): number {
    if (!value) {
      return Number.NEGATIVE_INFINITY;
    }
    const ts = Date.parse(value);
    return Number.isNaN(ts) ? Number.NEGATIVE_INFINITY : ts;
  }

  calculatePepStatus(toDate: string | null): string {
    if (!toDate) {
      return this.translate.instant('riskIndicators.statusActive');
    }

    const toDateObj = new Date(toDate);
    const today = new Date();
    const twelveMonthsAgo = new Date();
    twelveMonthsAgo.setMonth(today.getMonth() - 12);

    if (toDateObj >= twelveMonthsAgo && toDateObj <= today) {
      return this.translate.instant('riskIndicators.statusActiveLast12Months');
    } else if (toDateObj > today) {
      return this.translate.instant('riskIndicators.statusActive');
    }

    return this.translate.instant('riskIndicators.statusNotActive');
  }

  onShowRawJsonClick() {
    if (!this.client || !this.client.civilId) {
      console.warn('No civil ID available for PEP report');
      return;
    }

    // Fetch fresh data if not already loaded
    if (!this.pepReportRawData) {
      this.apisService.getPepReport(this.client.civilId).subscribe({
        next: (report) => {
          this.pepReportRawData = report;
          this.pepReportError = false;
          this.openJsonDialog(report);
        },
        error: (error) => {
          console.error('Error fetching PEP report:', error);
          this.pepReportRawData = null;
          this.pepReportError = true;
          this.openJsonDialog({ error: this.getApisUnavailableText() });
        },
      });
    } else {
      this.openJsonDialog(this.pepReportRawData);
    }
  }

  private openJsonDialog(data: any) {
    this.dialog.open(PepRawJsonDialogComponent, {
      width: '800px',
      maxHeight: '80vh',
      autoFocus: false,
      data: { report: data },
    });
  }

  private getApisUnavailableText(): string {
    return this.translate.instant('riskIndicators.apisUnavailable');
  }

  private addTagIfNeeded(tagCode: string) {
    if (!this.client || !this.client.id) {
      return;
    }

    if (this.client.tags && this.client.tags.length >= this.TAGS_LIMIT) {
      console.log('Tags limit reached, cannot add tag:', tagCode);
      return;
    }

    const hasTag = this.client.tags?.some((tag: Tag) => tag.code === tagCode);

    if (hasTag) {
      console.log('Tag already applied to client:', tagCode);
      return;
    }

    this.tagService
      .getTagByCode(tagCode)
      .pipe(
        mergeMap((tag: Tag) => {
          if (tag && tag.id) {
            return this.tagService.tagUsers([this.client.id], tag.id).pipe(
              tap(() => {
                if (!this.client.tags) {
                  this.client.tags = [];
                }
                this.client.tags.push(tag);

                this.tagService.onTagAddedOrDeleted.next(this.client.tags);
                this.tagService.onTagHttpRequest.next();

                console.log('Tag successfully added to client:', tagCode);
              })
            );
          }
          console.warn('Tag not found in system:', tagCode);
          return of(null);
        }),
        catchError((error) => {
          console.error('Error adding tag:', tagCode, error);
          return of(null);
        })
      )
      .subscribe();
  }

  createForm() {
    this.riskIndicatorsForm = this.fb.group({
      pep: [''],
      pepRelated: [''],
      companyParticipation: [''],
      seizure: [''],
      nap: [''],
      phoneMatch: [''],
    });
  }
}
